import { Button, Checkbox, Select, Tag, Tooltip } from 'antd';
import React, { useEffect, useRef, useState } from 'react';
import { StaffRole, StaffStaffSearchContainerStaffUsersSearchFieldsFragment } from '../../../../generated';
import FilterOptionLabel from '../../../shared/filter-option-label';
import { useSearchParams } from 'react-router-dom';
import { ReloadOutlined } from '@ant-design/icons';

const { Option } = Select;

const StaffSearchParamKey = {
  updatedAt: 'updatedAt',
  createdAt: 'createdAt',
  schemaVersion: 'schemaVersion',
  role: 'role',
  tags: 'tags',
  accessBlocked: 'accessBlocked',
  accessBlockedYesChecked: 'accessBlockedYesChecked',
  accessBlockedNoChecked: 'accessBlockedNoChecked',
  bothAccessBlocked: 'bothAccessBlocked'
};

const transformCount = (input: any) => {
  return Object.keys(input)
    .filter((key) => key !== '__typename')
    .reduce((acc: any, key) => {
      const values = input[key]?.reduce((subAcc: any, detail: any) => {
        subAcc[detail.value] = detail.count;
        return subAcc;
      }, {});
      acc[key] = values;
      return acc;
    }, {});
};

const updatesFiltersOptions = [
  { idSuffix: 'today', label: 'Today' },
  { idSuffix: 'yesterday', label: 'Yesterday' },
  { idSuffix: 'lastWeek', label: 'Within 7 days' },
  { idSuffix: 'lastMonth', label: 'Within 30 days' },
  { idSuffix: 'lastYear', label: 'Within 365 days' }
];

const updatesFilters = [
  {
    label: 'Last Updated',
    category: StaffSearchParamKey.updatedAt,
    items: updatesFiltersOptions
  },
  {
    label: 'Created At',
    category: StaffSearchParamKey.createdAt,
    items: updatesFiltersOptions
  }
];

const schemaVersionFilter = [{ value: '1.0.0', label: '1.0.0' }];

interface StaffSearchFilterProps {
  data: StaffStaffSearchContainerStaffUsersSearchFieldsFragment;
  roleList: StaffRole[];
  allTags: string[];
  refreshTagHandler: () => void;
}

interface StaffSearchFilterInterface {
  updatedAt: string[];
  createdAt: string[];
  schemaVersion: string[];
  role: string[];
  tags: string[];
  accessBlocked: boolean | null;
  accessBlockedYesChecked: boolean;
  accessBlockedNoChecked: boolean;
}

export const StaffSearchFilter: React.FC<StaffSearchFilterProps> = (props) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const count = transformCount(props?.data);

  const [checkedFilter, setCheckedFilter] = useState<StaffSearchFilterInterface>({
    updatedAt: searchParams.get(StaffSearchParamKey.updatedAt)?.split(',') || [],
    createdAt: searchParams.get(StaffSearchParamKey.createdAt)?.split(',') || [],
    schemaVersion: searchParams.get(StaffSearchParamKey.schemaVersion)?.split(',') || [],
    role: searchParams.get(StaffSearchParamKey.role)?.split(',') || [],
    tags: searchParams.get(StaffSearchParamKey.tags)?.split(',') || [],
    accessBlocked: searchParams.get(StaffSearchParamKey.accessBlocked) === 'true' ? true : searchParams.get(StaffSearchParamKey.accessBlocked) === 'false' ? false : null,
    accessBlockedYesChecked: searchParams.get(StaffSearchParamKey.accessBlocked) === 'true',
    accessBlockedNoChecked: searchParams.get(StaffSearchParamKey.accessBlocked) === 'false'
  });

  const hasInitialTagsSet = useRef(false);

  const handleFilterCheck = (event: any, filterCategory: keyof typeof checkedFilter) => {
    searchParams.set('page', '1');
    const { id: filterId, checked } = event.target;
    if (!checked) {
      const currentSearchCategory = searchParams.get(filterCategory);
      if (currentSearchCategory) {
        const updatedSearchCategory = currentSearchCategory.split(',').filter((item) => item !== filterId);
        if (updatedSearchCategory.length > 0) {
          searchParams.set(filterCategory, updatedSearchCategory.join(','));
        } else {
          searchParams.delete(filterCategory);
        }
        setSearchParams(searchParams);
      }
    } else {
      const currentSearchCategory = searchParams.get(filterCategory);
      if (currentSearchCategory) {
        const updatedSearchCategory = currentSearchCategory.split(',');
        updatedSearchCategory.push(filterId);
        searchParams.set(filterCategory, updatedSearchCategory.join(','));
        setSearchParams(searchParams);
      } else {
        searchParams.set(filterCategory, filterId);
        setSearchParams(searchParams);
      }
    }

    const filterName = filterId;
    setCheckedFilter((prevState) => {
      const currentCategory = prevState[filterCategory];

      if (Array.isArray(currentCategory)) {
        const updatedCategory = checked ? [...currentCategory, filterName] : currentCategory.filter((item: any) => item !== filterName);

        return {
          ...prevState,
          [filterCategory]: updatedCategory
        };
      }
      return prevState;
    });
  };

  const isFilterSelected = (idSuffix: string, category: string) => {
    return searchParams.get(category)?.split(',').includes(idSuffix);
  };

  const handleAccessBlockedChange = (isChecked: boolean, isYesOption: boolean) => {
    searchParams.set('page', '1');
    setCheckedFilter((prevState) => {
      let newAccessBlockedState: boolean | null;
      let newYesChecked = prevState.accessBlockedYesChecked;
      let newNoChecked = prevState.accessBlockedNoChecked;
      if (isYesOption) {
        newYesChecked = isChecked;
      } else {
        newNoChecked = isChecked;
      }
      if (newYesChecked && newNoChecked) {
        newAccessBlockedState = null;
        searchParams.set(StaffSearchParamKey.bothAccessBlocked, 'true');
      } else if (newYesChecked) {
        newAccessBlockedState = true;
        searchParams.delete(StaffSearchParamKey.bothAccessBlocked);
        newNoChecked = false;
      } else if (newNoChecked) {
        newAccessBlockedState = false;
        searchParams.delete(StaffSearchParamKey.bothAccessBlocked);
        newYesChecked = false;
      } else {
        newAccessBlockedState = null;
        searchParams.delete(StaffSearchParamKey.bothAccessBlocked);
      }
      if (newAccessBlockedState === null) {
        searchParams.delete(StaffSearchParamKey.accessBlocked);
      } else {
        searchParams.set(StaffSearchParamKey.accessBlocked, String(newAccessBlockedState));
      }
      setSearchParams(searchParams);
      return {
        ...prevState,
        accessBlocked: newAccessBlockedState,
        accessBlockedYesChecked: newYesChecked,
        accessBlockedNoChecked: newNoChecked
      };
    });
  };

  useEffect(() => {
    if (searchParams.get(StaffSearchParamKey.bothAccessBlocked) === 'true') {
      setCheckedFilter((prevState) => ({
        ...prevState,
        accessBlockedYesChecked: true,
        accessBlockedNoChecked: true
      }));
    }
  }, [searchParams]);

  useEffect(() => {
    if (props.data && count.tags && !hasInitialTagsSet.current) {
      hasInitialTagsSet.current = true;
    }
  }, [count.tags, props.data]);

  const clearAllFilter = () => {
    const filterKeys = Object.keys(StaffSearchParamKey) as (keyof StaffSearchFilterInterface)[];
    const hasFilters = filterKeys.some((key) => searchParams.get(key));

    if (!hasFilters) {
      return;
    }

    searchParams.delete(StaffSearchParamKey.bothAccessBlocked);

    filterKeys.forEach((key) => {
      searchParams.delete(key);
    });

    setSearchParams(searchParams);

    setCheckedFilter((prevState) => ({
      ...prevState,
      updatedAt: [],
      createdAt: [],
      schemaVersion: [],
      role: [],
      tags: [],
      accessBlocked: null,
      accessBlockedYesChecked: false,
      accessBlockedNoChecked: false
    }));
  };

  const openFilter = (filterCategory: keyof typeof checkedFilter) => {
    const filterValue = checkedFilter[filterCategory];
    if (filterCategory === StaffSearchParamKey.accessBlocked) {
      if (
        (checkedFilter.accessBlockedYesChecked || searchParams.get(StaffSearchParamKey.bothAccessBlocked) == 'true') &&
        (checkedFilter.accessBlockedNoChecked || searchParams.get(StaffSearchParamKey.bothAccessBlocked) == 'true')
      ) {
        return true;
      }
      return checkedFilter.accessBlockedYesChecked || checkedFilter.accessBlockedNoChecked;
    } else {
      return Array.isArray(filterValue) && filterValue.length > 0;
    }
  };

  const handleSelectChange = (paramName: keyof typeof checkedFilter, value: string[]) => {
    searchParams.set('page', '1');
    const paramValue = value.join(',');
    paramValue ? searchParams.set(paramName, paramValue) : searchParams.delete(paramName);
    setSearchParams(searchParams);
    setCheckedFilter((prevState) => ({
      ...prevState,
      [paramName]: value
    }));
  };

  return (
    <div className="flex-[1_1_30%] mr-[10px] min-w-[220px] max-w-[300px] max-h-[200vh] overflow-x-hidden">
      <div style={{ marginBottom: '15px' }}>
        <Button onClick={clearAllFilter} type="primary">
          Clear All Filters
        </Button>
      </div>
      <div className="overflow-x-hidden overflow-y-auto max-h-[750px] scrollbar-hide">
        <div className="flex flex-wrap w-[200px] mb-2">
          {checkedFilter?.tags.map((tag) => (
            <Tag
              className="mb-2"
              key={tag}
              closable
              onClose={() => {
                handleFilterCheck({ target: { checked: false, id: tag } }, 'tags');
              }}
            >
              {tag}
            </Tag>
          ))}
        </div>
        <FilterOptionLabel label="State" initiallyOpen={openFilter(StaffSearchParamKey.accessBlocked as keyof StaffSearchFilterInterface)}>
          <Checkbox
            id="accessBlockedYes"
            checked={checkedFilter.accessBlockedYesChecked || searchParams.get(StaffSearchParamKey.bothAccessBlocked) == 'true'}
            onChange={(event: any) => handleAccessBlockedChange(event.target.checked, true)}
          >
            <p className="checkBoxLabel">Access Restricted ({count[StaffSearchParamKey.accessBlocked]?.true ?? 0})</p>
          </Checkbox>

          <Checkbox
            id="accessBlockedNo"
            checked={checkedFilter.accessBlockedNoChecked || searchParams.get(StaffSearchParamKey.bothAccessBlocked) == 'true'}
            onChange={(event: any) => handleAccessBlockedChange(event.target.checked, false)}
          >
            <p className="checkBoxLabel">Not Restricted ({count[StaffSearchParamKey.accessBlocked]?.false ?? 0})</p>
          </Checkbox>
        </FilterOptionLabel>

        <FilterOptionLabel label="Role" initiallyOpen={openFilter(StaffSearchParamKey.role as keyof StaffSearchFilterInterface)}>
          <Select
            allowClear
            mode="multiple"
            style={{ width: '200px' }}
            placeholder="Select Role"
            value={checkedFilter.role}
            onChange={(value) => {
              searchParams.set('page', '1');
              setCheckedFilter((prevState) => ({
                ...prevState,
                role: value
              }));
              const roles = value.join(',');
              if (roles) {
                searchParams.set(StaffSearchParamKey.role, roles);
              } else {
                searchParams.delete(StaffSearchParamKey.role);
              }
              setSearchParams(searchParams);
            }}
          >
            {props.roleList.map((role: any) => (
              <Option key={role.id} value={role.id}>
                {role.roleName} ({count[StaffSearchParamKey.role]?.[role.id] ?? 0})
              </Option>
            ))}
          </Select>
        </FilterOptionLabel>

        <FilterOptionLabel label="Tag" initiallyOpen={openFilter('tags')} icon={<ReloadOutlined />} iconHandler={props?.refreshTagHandler} iconToolTipText="Refresh Tags">
          <Select
            mode="multiple"
            style={{ width: '200px' }}
            allowClear
            placeholder="Select Tag"
            value={checkedFilter.tags}
            onChange={(value) => handleSelectChange('tags', value)}
          >
            {props?.allTags?.map((tag) => (
              <Option key={tag} value={tag}>
                <Tooltip placement="right" title={`${tag} (${count['tags']?.[tag] ?? 0})`}>
                  {tag}({count['tags']?.[tag] ?? 0})
                </Tooltip>
              </Option>
            ))}
          </Select>
        </FilterOptionLabel>

        {updatesFilters.map((filter) => (
          <FilterOptionLabel key={filter.category} label={filter.label} initiallyOpen={openFilter(filter.category as keyof StaffSearchFilterInterface)}>
            {updatesFiltersOptions.map((option) => (
              <Checkbox
                key={option.idSuffix}
                id={option.idSuffix}
                checked={isFilterSelected(option.idSuffix, filter.category)}
                onChange={(event: any) => {
                  handleFilterCheck(event, filter.category as keyof typeof checkedFilter);
                }}
              >
                <p className="checkBoxLabel">
                  {option.label} ({count[filter.category]?.[option.idSuffix] ?? 0})
                </p>
              </Checkbox>
            ))}
          </FilterOptionLabel>
        ))}

        <FilterOptionLabel label="Schema Version" initiallyOpen={openFilter(StaffSearchParamKey.schemaVersion as keyof StaffSearchFilterInterface)}>
          {schemaVersionFilter.map((option) => (
            <Checkbox
              key={option.value}
              id={option.value}
              checked={isFilterSelected(option.value, StaffSearchParamKey.schemaVersion)}
              onChange={(event: any) => {
                handleFilterCheck(event, StaffSearchParamKey.schemaVersion as keyof typeof checkedFilter);
              }}
            >
              <p className="checkBoxLabel">
                {option.label} ({count[StaffSearchParamKey.schemaVersion]?.[option.value] ?? 0})
              </p>
            </Checkbox>
          ))}
        </FilterOptionLabel>
      </div>
    </div>
  );
};
